import { orderProviderToOrderPrefix } from "@redotech/redo-order-common/order-prefix";
import { Provider } from "@redotech/redo-order-common/provider";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum ExternalIntegrationIdProviders {
  AIMS360 = "aims360",
  APPAREL_MAGIC = "apparelmagic",
  BLUE_CHERRY = "bluecherry",
  BRIGHTPEARL = "brightpearl",
  CIN7_OMNI = "cin7omni",
  CLOUDX = "cloudx",
  EXTENSIV = "extensiv",
  FLEXPORT = "flexport",
  GLOBALE = "globale",
  NETSUITE = "netsuite",
  SFOM = "sfom",
  SHIPBOB = "shipbob",
  SHIPEDGE = "shipedge",
  SHIPHERO = "shiphero",
  SHIPMONK = "shipmonk",
  TRACKSTAR = "trackstar",
  RYDER = "ryder",
  RETURNBEAR = "returnbear",
  BLUEYONDER = "blueyonder",
  INMAR = "inmar",
  DEPOSCO = "deposco",
  MASONHUB = "masonhub",
  MINTSOFT = "mintsoft",
  ECWID = "ecwid",
}

export enum ExternalIntegrationIdTypes {
  // Primary return document
  RMA = "rma",

  // Support documents
  SUPPORT_CASE = "support_case",

  // Financial documents
  CASH_REFUND = "cash_refund",
  CREDIT_MEMO = "credit_memo",
  REFUND = "refund",
  SALES_CREDIT = "sales_credit",

  // Fulfillment documents
  RECEIPT = "receipt",
  SALES_ORDER = "sales_order",

  // Vendor documents
  VENDOR_RETURN_AUTHORIZATION = "vendor_return_authorization",

  // Physical package identifiers
  BOX = "box",
  BOX_BARCODE = "box_barcode",
}

const externalIntegrationIdSchema = z.object({
  provider: createEnum(ExternalIntegrationIdProviders, {
    enumName: "ExternalIntegrationIdProviders",
  }),
  type: createEnum(ExternalIntegrationIdTypes, {
    enumName: "ExternalIntegrationIdTypes",
  }),
  id: z.string(),
  shipmentGroupId: z.string().nullish(),
});
export type ExternalIntegrationId = z.infer<typeof externalIntegrationIdSchema>;

const providerAllowedTypes = {
  [ExternalIntegrationIdProviders.AIMS360]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.BLUE_CHERRY]: [
    ExternalIntegrationIdTypes.RMA,
  ],
  [ExternalIntegrationIdProviders.APPAREL_MAGIC]: [
    ExternalIntegrationIdTypes.RMA,
    ExternalIntegrationIdTypes.CREDIT_MEMO,
  ],
  [ExternalIntegrationIdProviders.BRIGHTPEARL]: [
    ExternalIntegrationIdTypes.SALES_CREDIT,
  ],
  [ExternalIntegrationIdProviders.CIN7_OMNI]: [
    ExternalIntegrationIdTypes.SALES_CREDIT,
  ],
  [ExternalIntegrationIdProviders.CLOUDX]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.EXTENSIV]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.FLEXPORT]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.GLOBALE]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.NETSUITE]: [
    ExternalIntegrationIdTypes.RMA,
    ExternalIntegrationIdTypes.SUPPORT_CASE,
    ExternalIntegrationIdTypes.RECEIPT,
    ExternalIntegrationIdTypes.CASH_REFUND,
    ExternalIntegrationIdTypes.CREDIT_MEMO,
    ExternalIntegrationIdTypes.REFUND,
    ExternalIntegrationIdTypes.SALES_ORDER,
    ExternalIntegrationIdTypes.VENDOR_RETURN_AUTHORIZATION,
  ],
  [ExternalIntegrationIdProviders.SFOM]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.RYDER]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.SHIPBOB]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.SHIPEDGE]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.SHIPHERO]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.SHIPMONK]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.TRACKSTAR]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.RETURNBEAR]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.BLUEYONDER]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.INMAR]: [
    ExternalIntegrationIdTypes.BOX,
    ExternalIntegrationIdTypes.BOX_BARCODE,
  ],
  [ExternalIntegrationIdProviders.DEPOSCO]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.MASONHUB]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.MINTSOFT]: [ExternalIntegrationIdTypes.RMA],
  [ExternalIntegrationIdProviders.ECWID]: [ExternalIntegrationIdTypes.RMA],
} as const satisfies Record<
  ExternalIntegrationIdProviders,
  readonly ExternalIntegrationIdTypes[]
>;

type AllowedTypesFor<P extends ExternalIntegrationIdProviders> =
  (typeof providerAllowedTypes)[P][number];

export type ValidProviderTypePair = {
  [P in ExternalIntegrationIdProviders]: {
    provider: P;
    type: AllowedTypesFor<P>;
  };
}[ExternalIntegrationIdProviders];

export type FindExternalIntegrationIdParams = ValidProviderTypePair & {
  shipmentGroupId?: string;
  id?: string;
};

export type ValidExternalIntegrationId = ValidProviderTypePair & {
  id: string;
  shipmentGroupId?: string | null;
};

export function externalIntegrationId(
  params: ValidExternalIntegrationId,
): ExternalIntegrationId {
  return params;
}

function matchesExternalIntegrationIdParams(
  extId: ExternalIntegrationId,
  params: FindExternalIntegrationIdParams,
): boolean {
  if (extId.provider !== params.provider) {
    return false;
  }
  if (extId.type !== params.type) {
    return false;
  }
  if (
    params.shipmentGroupId !== undefined &&
    extId.shipmentGroupId !== params.shipmentGroupId
  ) {
    return false;
  }
  if (params.id !== undefined && extId.id !== params.id) {
    return false;
  }
  return true;
}

export function findExternalIntegrationId(
  externalIntegrationIds: ExternalIntegrationId[] | undefined | null,
  params: FindExternalIntegrationIdParams,
): ExternalIntegrationId | undefined {
  if (!externalIntegrationIds) {
    return undefined;
  }
  return externalIntegrationIds.find((extId) =>
    matchesExternalIntegrationIdParams(extId, params),
  );
}

export function filterExternalIntegrationIds(
  externalIntegrationIds: ExternalIntegrationId[] | undefined | null,
  params: FindExternalIntegrationIdParams,
): ExternalIntegrationId[] {
  if (!externalIntegrationIds) {
    return [];
  }
  return externalIntegrationIds.filter((extId) =>
    matchesExternalIntegrationIdParams(extId, params),
  );
}

export function hasExternalIntegrationId(
  externalIntegrationIds: ExternalIntegrationId[] | undefined | null,
  params: FindExternalIntegrationIdParams,
): boolean {
  return (
    findExternalIntegrationId(externalIntegrationIds, params) !== undefined
  );
}

/**
 * Strips the provider-specific prefix (e.g. "BC-" for BigCommerce) from an
 * order name. During order sync we prepend these prefixes, but external
 * integrations like Trackstar store the original order name without it.
 */
export function stripProviderOrderPrefix(
  orderName: string,
  provider?: Provider | null,
): string {
  if (provider) {
    const prefix = orderProviderToOrderPrefix[provider];
    if (prefix && orderName.startsWith(prefix)) {
      return orderName.slice(prefix.length);
    }
  }
  return orderName;
}
